import { AdminOrganizationRow } from './admin-organization.model';
import { AdminCallRow } from './admin-call.model';

export interface AdminOrgLifetime {
  requests: number;
  credits: number;
  costCents: number;
  /** Null for an org that has never made a call. */
  firstCallAt: string | null;
  lastCallAt: string | null;
}

/** The `organization` object as returned both nested in GET .../:id and
 *  standalone from PATCH .../:id a superset of the list row (same fields
 *  plus id, members_count, api_keys_count, cikisi_checked_at, lifetime),
 *  so this extends AdminOrganizationRow rather than duplicating it. */
export class AdminOrganizationDetailOrg extends AdminOrganizationRow {
  membersCount!: number;
  /** Active keys only, per the contract api_keys[] below also lists revoked ones. */
  apiKeysCount!: number;
  cikisiCheckedAt: string | null = null;
  lifetime!: AdminOrgLifetime;

  constructor(data: any = {}) {
    super(data);
    this.membersCount = data.members_count;
    this.apiKeysCount = data.api_keys_count;
    this.cikisiCheckedAt = data.cikisi_checked_at ?? null;
    const lifetime = data.lifetime ?? {};
    this.lifetime = {
      requests: lifetime.requests,
      credits: lifetime.credits,
      costCents: lifetime.cost_cents,
      firstCallAt: lifetime.first_call_at ?? null,
      lastCallAt: lifetime.last_call_at ?? null,
    };
  }
}

export interface AdminOrgMemberUser {
  id: number;
  email: string;
  name: string;
  confirmed: boolean;
  createdAt: string;
}

export class AdminOrgMember {
  id!: number;
  role!: string;
  createdAt!: string;
  user!: AdminOrgMemberUser;

  constructor(data: any = {}) {
    this.id = data.id;
    this.role = data.role;
    this.createdAt = data.created_at;
    const user = data.user ?? {};
    this.user = {
      id: user.id,
      email: user.email,
      name: user.name,
      confirmed: !!user.confirmed,
      createdAt: user.created_at,
    };
  }
}

export interface AdminOrgKeyCreatedBy {
  email: string;
  name: string;
}

/** Detail-page api key shape id IS the key's UUID here (per the contract's
 *  ids gotcha), unlike AdminCallRow.apiKey where id is the numeric PK and
 *  uid is the UUID two different shapes for "the same" key depending on
 *  where it's nested. Never assume one matches the other's field names.
 *  Full secrets are never returned; prefix is all there is to show. */
export class AdminOrgApiKey {
  id!: string;
  name!: string;
  prefix!: string;
  status!: string;
  lastUsedAt: string | null = null;
  revokedAt: string | null = null;
  createdAt!: string;
  createdBy: AdminOrgKeyCreatedBy | null = null;

  constructor(data: any = {}) {
    this.id = data.id;
    this.name = data.name;
    this.prefix = data.prefix;
    this.status = data.status;
    this.lastUsedAt = data.last_used_at ?? null;
    this.revokedAt = data.revoked_at ?? null;
    this.createdAt = data.created_at;
    this.createdBy = data.created_by ?? null;
  }

  get isRevoked(): boolean {
    return this.status === 'revoked' || !!this.revokedAt;
  }
}

/** GET /admin/organizations/:id's full response. recent_calls is "the last
 *  20 calls, same shape as the Calls route", so it reuses AdminCallRow. */
export class AdminOrganizationDetailResult {
  organization!: AdminOrganizationDetailOrg;
  members: AdminOrgMember[] = [];
  /** Active keys first, then revoked per the contract; not re-sorted here. */
  apiKeys: AdminOrgApiKey[] = [];
  recentCalls: AdminCallRow[] = [];

  constructor(data: any = {}) {
    this.organization = new AdminOrganizationDetailOrg(data.organization);
    this.members = (data.members ?? []).map((m: any) => new AdminOrgMember(m));
    this.apiKeys = (data.api_keys ?? []).map((k: any) => new AdminOrgApiKey(k));
    this.recentCalls = (data.recent_calls ?? []).map((c: any) => new AdminCallRow(c));
  }
}
